import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App";

const style = document.createElement("style");
style.textContent = `
  :root {
    --blue: #0000FF;
    --ink: #0A0A0A;
    --paper: #FFFFFF;
    --muted: #6B6B6B;
    --line: #E5E5E5;
    --line-strong: #0A0A0A;
  }
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html, body {
    background: var(--paper); color: var(--ink);
    font-family: 'Geist', -apple-system, BlinkMacSystemFont, sans-serif;
    -webkit-font-smoothing: antialiased; text-rendering: optimizeLegibility;
  }
  body { overflow-x: hidden; }
  a { color: inherit; text-decoration: none; }
  button { font-family: inherit; cursor: pointer; border: none; background: none; color: inherit; }
  input, textarea, select { font-family: inherit; color: inherit; }
  ::selection { background: var(--blue); color: #fff; }
  ::-webkit-scrollbar { width: 6px; }
  ::-webkit-scrollbar-track { background: var(--paper); }
  ::-webkit-scrollbar-thumb { background: var(--ink); border-radius: 0; }

  /* Logo mark with blue corner dot */
  .logo-mark {
    width: 40px; height: 40px; background: #fff; border: 1px solid var(--ink);
    display: flex; align-items: center; justify-content: center;
    padding: 3px; position: relative; flex: none;
  }
  .logo-mark img { display: block; width: 100%; height: 100%; object-fit: contain; mix-blend-mode: multiply; }
  .logo-mark::after { content: ""; position: absolute; right: -4px; bottom: -4px; width: 8px; height: 8px; background: var(--blue); }

  /* Nav links */
  .nav-link {
    position: relative; padding: 6px 2px;
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 13px; letter-spacing: 0.12em; text-transform: uppercase;
  }
  .nav-link::after { content: ""; position: absolute; left: 0; right: 0; bottom: -2px; height: 2px; background: transparent; transition: background 0.15s; }
  .nav-link:hover::after, .nav-link.active::after { background: var(--blue); }

  /* Buttons */
  .btn {
    display: inline-flex; align-items: center; gap: 12px;
    padding: 16px 22px; border: 1px solid var(--ink);
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 12px; letter-spacing: 0.1em; text-transform: uppercase;
    background: var(--blue); border-color: var(--blue); color: #fff; transition: all 0.2s; cursor: pointer;
  }
  .btn:hover { background: #0000cc; border-color: #0000cc; }
  .btn.btn-ghost { background: transparent; color: var(--ink); }
  .btn.btn-ghost:hover { background: var(--ink); color: #fff; }
  .btn.btn-sm { padding: 12px 16px; font-size: 11px; }

  /* Placeholder blocks */
  .ph {
    position: relative; width: 100%; height: 100%;
    background-color: #F2F2F2;
    background-image: repeating-linear-gradient(45deg, transparent 0 10px, rgba(10,10,10,0.04) 10px 11px);
    overflow: hidden; display: flex; align-items: flex-end; justify-content: flex-start;
  }
  .ph::before { content: ""; position: absolute; inset: 0; border: 1px solid rgba(10,10,10,0.08); pointer-events: none; }
  .ph-label {
    font-family: 'JetBrains Mono', monospace; font-size: 10px;
    letter-spacing: 0.08em; text-transform: uppercase; color: rgba(10,10,10,0.55);
    padding: 10px 12px; background: rgba(255,255,255,0.85); margin: 10px;
  }
  .ph.dark { background-color: #111; background-image: repeating-linear-gradient(45deg, transparent 0 10px, rgba(255,255,255,0.05) 10px 11px); }
  .ph.blue { background-color: var(--blue); background-image: repeating-linear-gradient(45deg, transparent 0 10px, rgba(255,255,255,0.1) 10px 11px); }
  .ph.photo { background-image: var(--photo, none); background-size: cover; background-position: center; }
  .ph.photo::after { content: ""; position: absolute; inset: 0; background: linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.4) 100%); pointer-events: none; }
  .ph.photo .ph-label, .ph.dark .ph-label, .ph.blue .ph-label { color: rgba(255,255,255,0.85); background: rgba(0,0,0,0.5); position: relative; z-index: 1; }

  /* Marquee */
  @keyframes marquee-scroll { to { transform: translateX(-50%); } }
  .marquee-track { display: flex; gap: 56px; padding: 18px 0; animation: marquee-scroll 40s linear infinite; white-space: nowrap; }
  .marquee-item { font-family: 'JetBrains Mono', monospace; font-size: 12px; letter-spacing: 0.14em; text-transform: uppercase; color: var(--muted); display: flex; align-items: center; gap: 56px; }
  .marquee-item::after { content: "◆"; color: var(--blue); font-size: 8px; }

  /* Section tag */
  .section-tag { font-family: 'JetBrains Mono', monospace; font-size: 11px; letter-spacing: 0.14em; text-transform: uppercase; color: var(--blue); }

  /* Hero tech overlay labels */
  .tech-label { font-family: 'JetBrains Mono', monospace; font-size: 10px; color: #fff; background: rgba(0,0,0,0.6); padding: 4px 8px; letter-spacing: 0.1em; }

  /* Service cards hover */
  .service-card { transition: background 0.2s, color 0.2s; }
  .service-card:hover { background: var(--ink) !important; color: #fff !important; }
  .service-card:hover .svc-meta { color: inherit !important; }
  .service-card:hover .svc-sym { color: var(--blue) !important; }
  .service-card:hover p { color: inherit !important; opacity: 0.8; }

  /* Project cards hover */
  .project-card { transition: background 0.25s, color 0.25s; cursor: pointer; }
  .project-card:hover { background: var(--ink) !important; color: #fff !important; }
  .project-card:hover .project-tag { border-color: rgba(255,255,255,0.3) !important; color: #fff !important; }
  .project-card:hover .project-meta { color: rgba(255,255,255,0.6) !important; }

  /* Filter buttons */
  .filter-btn { font-family: 'JetBrains Mono', monospace; font-size: 11px; letter-spacing: 0.1em; text-transform: uppercase; padding: 10px 14px; border: 1px solid var(--ink); background: #fff; transition: all 0.15s; cursor: pointer; }
  .filter-btn:hover { background: var(--ink); color: #fff; }
  .filter-btn.active { background: var(--blue); border-color: var(--blue); color: #fff; }
  .filter-btn.active .fcount { color: rgba(255,255,255,0.6); }

  /* Inquiry form */
  .inquiry-field-input {
    width: 100%; padding: 10px 0; border: none; border-bottom: 1px solid var(--ink);
    font-size: 15px; background: transparent; outline: none; transition: border-color 0.2s; font-family: inherit;
  }
  .inquiry-field-input:focus { border-color: var(--blue); }
  .inquiry-field-input.has-error { border-color: #d00; }
  .inquiry-textarea {
    width: 100%; min-height: 100px; resize: vertical; border: 1px solid var(--ink);
    padding: 12px; font-size: 15px; background: transparent; outline: none; transition: border-color 0.2s; font-family: inherit;
  }
  .inquiry-textarea:focus { border-color: var(--blue); }
  .inquiry-textarea.has-error { border-color: #d00; }
  .svc-chip {
    font-family: 'JetBrains Mono', monospace; font-size: 10px; letter-spacing: 0.1em; text-transform: uppercase;
    padding: 8px 12px; border: 1px solid var(--ink); background: #fff; cursor: pointer; user-select: none; transition: all 0.15s;
  }
  .svc-chip:hover { background: var(--ink); color: #fff; }
  .svc-chip.selected { background: var(--blue); border-color: var(--blue); color: #fff; }

  /* Footer map hover */
  .fv-map { display: grid; grid-template-columns: 1.3fr 1fr; border: 1px solid var(--ink); text-decoration: none; color: inherit; overflow: hidden; transition: all 0.2s; cursor: pointer; }
  .fv-map:hover { box-shadow: 6px 6px 0 var(--ink); transform: translate(-3px,-3px); }

  /* Footer nav link hover */
  .fv-link:hover { color: var(--blue); }

  /* Phone link hover */
  .ph-line:hover { color: var(--blue); }

  /* Responsive */
  @media (max-width: 900px) {
    .nav-links-list { display: none !important; }
    .nav-inner { grid-template-columns: auto auto !important; }
    .hero-grid { grid-template-columns: 1fr !important; }
    .hero-left { border-right: none !important; border-bottom: 1px solid var(--ink) !important; padding: 48px 24px !important; }
    .hero-right { min-height: 360px; }
    .about-grid { grid-template-columns: 1fr !important; gap: 40px !important; }
    .contact-grid { grid-template-columns: 1fr !important; gap: 40px !important; }
    .project-grid { grid-template-columns: 1fr !important; }
    .projects-head { grid-template-columns: 1fr !important; }
    .footer-grid { grid-template-columns: 1fr 1fr !important; }
  }
`;
document.head.appendChild(style);

const link = document.createElement("link");
link.rel = "stylesheet";
link.href = "https://fonts.googleapis.com/css2?family=Geist:wght@300;400;500;600;700;800;900&family=JetBrains+Mono:wght@400;500;600&display=swap";
document.head.appendChild(link);

ReactDOM.createRoot(document.getElementById("root")).render(<App />);
